import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { styled } from '@mui/material/styles'
import {
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TableFooter,
  TablePagination,
  Paper,
  Checkbox,
  Stack,
  Button,
  LinearProgress,
  Tooltip,
  TableCell,
  Menu,
  MenuItem,
  Typography,
  Divider,
} from '@mui/material'

import Emitter from '@utils/eventEmitter'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {formatUnderFromCamel} from '@utils/formatText'
import Modal from '@views/employee/modal/FormModal'
import BulkModal from '@views/employee/modal/BulkModal'
import DynamicHead from '@components/DynamicHead'

import {
  getMemberList,
  createMember,
  updateMember,
  downloadMemberAttach,
  bulkUpdateMemberAttach
} from '@store/member'

const ButtonGroup = styled(Stack)({
  padding: '10px 0',
  textAlign: 'right'
})

const headerList = [
  {key: '', name: '', sort: false, checkbox: true},
  {key: 'companyName', name: '기업명', sort: true, search: true},
  {key: 'position', name: '직책', sort: false},
  {key: 'name', name: '임원 성명', sort: false},
  {key: 'birth', name: '생년월일', sort: true, search: true},
  {key: 'signExpectDate', name: '선임(예정)일', sort: true, search: true},
  {key: 'resignExpectDate', name: '사임(예정)일', sort: true, search: true},
  {key: 'personalInfoAgreeName', name: '개인정보 동의서', sort: true, search: true},
  {key: 'noloanAgreeName', name: '대출 불가 확약서', sort: true, search: true},
  {key: 'corporateRegistrationName', name: '법인등기부등본', sort: true, search: true},
]

const initParams = {
  page: 0,
  rowsPerPage: 10,
  sortDirection: null
}

const EmployeeTable = () => {
  const dispatch = useDispatch()
  const {
    memberList,
    memberListTotalSize,
  } = useSelector(store => store.member)
  const [ checkeds, setCheckeds ] = useState({})
  const [ modalOpen, setModalOpen ] = useState(false)
  const [ bulkModalOpen, setBulkModalOpen ] = useState(false)
  const [ editData, setEditData ] = useState(null)
  const [ bulkEditData, setBulkEditData ] = useState(null)
  const [ params, setParams ] = useState(initParams)
  const [ loading, setLoading ] = useState(false)

  const handleChangePage = (e, newPage) => {
    setParams(Object.assign({}, params, {page: newPage}))
  }

  const handleChangeRowsPerPage = (e) => {
    const currentPageSize = parseInt(e.target.value, 10)
    setParams(Object.assign({}, params, {page: 0, rowsPerPage: currentPageSize}))
  }

  const onSelectAllClick = () => {
    let updateCheckeds = {}
    if (!isAllChecked) {
      memberList.forEach(data => {
        updateCheckeds[data.companyMemberCode] = true
      })
    }
    setCheckeds(updateCheckeds)
  }

  const changeChecked = (itemId) => {
    let updateCheckeds = Object.assign({}, checkeds)
    if (checkeds[itemId]) {
      updateCheckeds = Object.assign({}, checkeds)
      delete updateCheckeds[itemId]
    } else {
      updateCheckeds[itemId] = true
    }
    setCheckeds(updateCheckeds)
  }

  const isAllChecked = useMemo(() => {
    if (!memberList.length) return false;
    return Object.keys(checkeds).length === memberList.length
  }, [ checkeds, memberList ])

  const completeModal = async (action, data, companyMemberCode) => {
    setModalOpen(false)

    if (action) {
      if (companyMemberCode) {
        dispatch(updateMember({
          form: data,
          companyMemberCode
        }))
      } else {
        await dispatch(createMember(data))
        onGetMemberList()
      }
    }
  }

  const downloadFile = (memberCode, companyMemberCode, attachType, name) => {
    dispatch(downloadMemberAttach({
      memberCode,
      companyMemberCode,
      type: attachType,
      fileName: name,
    }))
  }

  const openCreateModal = () => {
    setCheckeds({})
    setEditData(null)
    setModalOpen(true)
  }

  const openEditModal = () => {
    const checkedList = Object.keys(checkeds)

    if (checkedList.length === 0) {
      Emitter.emit('SHOW_MESSAGE', {
        text: '수정 할 임원을 선택하세요.',
        color: 'warning'
      })
    } else if (checkedList.length > 1) {
      Emitter.emit('SHOW_MESSAGE', {
        text: '한명의 임원을 선택하세요.',
        color: 'warning'
      })
    } else {
      setEditData(memberList.find(member => member.companyMemberCode === checkedList[0]))
      setModalOpen(true)
    }
  }

  const openBulkCreateModal = () => {
    const checkedList = Object.keys(checkeds)
    if (checkedList.length === 0) {
      Emitter.emit('SHOW_MESSAGE', {
        text: '수정 할 임원을 선택하세요.',
        color: 'warning'
      })
    } else {
      setBulkEditData(memberList.filter(member => checkedList.includes(member.companyMemberCode)))
      setBulkModalOpen(true)
    }
  }

  const completeBulkModal = (action, data) => {
    if (action) {
      if (data) {
        dispatch(bulkUpdateMemberAttach({
          form: data,
        }))
      }
    }

    setBulkModalOpen(false)
  }

  const changeDirection = (key, value) => {
    const tempDirection = {}
    tempDirection[key] = value
    setParams(Object.assign({}, params, {
      page: 0,
      sortDirection: tempDirection
    }))
  }

  const getFilterSort = useCallback(() => {
    let sort = []
    const {sortDirection} = params;
    if (sortDirection) {
      sort = Object.keys(sortDirection)
        .filter(key => sortDirection[key])
        .map(key => {
          const sortType = formatUnderFromCamel({text: key});
          return `${sortType},${sortDirection[key]}`
        })
    }
    return sort.length ? sort : undefined
  }, [params])

  const onGetMemberList = useCallback(async () => {
    const {page, rowsPerPage} = params
    const resultParams = {
      page,
      size: rowsPerPage,
      sort: getFilterSort(),
    }
    setLoading(true)
    await dispatch(getMemberList(resultParams))
    setLoading(false)
  }, [dispatch, params, getFilterSort])

  useEffect(() => onGetMemberList(), [onGetMemberList])

  return (
    <>
      <TableContainer component={Paper} sx={{position: 'relative'}}>
        {loading && <LinearProgress sx={{position: 'absolute', width: '100%'}}/>}
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <DynamicHead 
            list={headerList}
            sortDirection={params.sortDirection}
            changeDirection={changeDirection}
            isAllChecked={isAllChecked}
            onSelectAllClick={onSelectAllClick}
          />
          <TableBody>
            {
              memberList && memberList.map((item) => (
                <RoleTableItem 
                  item={item} 
                  key={item.companyMemberCode}
                  checked={checkeds[item.companyMemberCode]}
                  changeChecked={changeChecked}
                  downloadFile={downloadFile}
                />
              ))
            }
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                  count={memberListTotalSize}
                  page={params.page}
                  rowsPerPage={params.rowsPerPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 30, 50]}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <CompanyList />
      
      <ButtonGroup
        direction="row"
        justifyContent="flex-end"
        spacing={1}
      >
        <Button variant="contained" disableElevation onClick={() => openCreateModal()}>등록</Button>
        <Button variant="contained" disableElevation onClick={() => openEditModal()}>수정</Button>
        <Button variant="contained" disableElevation onClick={() => openBulkCreateModal()}>등본 일괄등록</Button>
      </ButtonGroup>

      {modalOpen && <Modal
        data={editData}
        open={modalOpen}
        completeModal={completeModal}
      />}

      {bulkModalOpen && <BulkModal
        memberList={bulkEditData}
        open={bulkModalOpen}
        completeModal={completeBulkModal}
      />}
    </>
  )
}

const RoleTableItem = ({
  item,
  checked,
  changeChecked,
  downloadFile
}) => {
  return (
    <TableRow>
      <TableCell padding="checkbox" align="center">
        <Checkbox
          color="primary"
          checked={checked || false}
          onChange={() => changeChecked(item.companyMemberCode)}
        />
      </TableCell>
      <TableCell component="th" scope="row" align="center">
        {item.companyName}
      </TableCell>
      <TableCell align="center">{item.position}</TableCell>
      <TableCell align="center">{item.name}</TableCell>
      <TableCell align="center">{item.birth}</TableCell>
      <TableCell align="center">{item.signExpectDate}</TableCell>
      <TableCell align="center">{item.resignExpectDate}</TableCell>
      <TableCell align="center">
        {
          item.attachInfo?.personalInfoAgreeName && (
            <Tooltip title={item.attachInfo?.personalInfoAgreeName}>
              <Button
                onClick={() => downloadFile(item.memberCode, item.companyMemberCode, 'PERSONAL_INFO_AGREE', item.attachInfo?.personalInfoAgreeName)}
                variant="outlined"
                className="attachFile-btn"
              >
                {item.attachInfo?.personalInfoAgreeName}
              </Button>
            </Tooltip>
          )
        }
      </TableCell>
      <TableCell align="center">
        {
          item.attachInfo?.noloanAgreeName && (
            <Tooltip title={item.attachInfo?.noloanAgreeName}>
              <Button
                onClick={() => downloadFile(item.memberCode, item.companyMemberCode, 'NOLOAN_AGREE', item.attachInfo?.noloanAgreeName)}
                variant="outlined"
                className="attachFile-btn"
              >
                {item.attachInfo?.noloanAgreeName}
              </Button>
            </Tooltip>
          )
        }
      </TableCell>
      <TableCell align="center">
        {
          item.corporateRegistrationName && (
            <Tooltip title={item.corporateRegistrationName}>
              <Button
                onClick={() => downloadFile(item.memberCode, item.companyMemberCode, 'CORPORATE_REGISTRATION', item.corporateRegistrationName)}
                variant="outlined"
                className="attachFile-btn"
              >
                {item.corporateRegistrationName}
              </Button>
            </Tooltip>
          )
        }
      </TableCell>
    </TableRow>
  )
}

const CompanyList = () => {
  const {
    historyCompanyList,
  } = useSelector(store => store.report)
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <Button
        variant="outlined"
        onClick={handleClick}
        sx={{float: 'left', mt: 1.5}}
      >
        주간 변동 예정 등록 확인 <ArrowDropDownIcon />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <CompanyItem 
          companyName={'기업명'}
          memberTitle={'임원변동'}
          companyTitle={'기업변동'}
          backgroundColor={'#eee'}
        />
        {
          historyCompanyList.map((data, index) => {
            const {companyName, member, company} = data
            return <CompanyItem 
              key={index}
              companyName={companyName}
              member={member ? '( Y )' : '( N )'}
              company={company ? '( Y )' : '( N )'}
            />
          })
        }
      </Menu>
    </div>
  );
}

const CompanyItem = ({companyName, member, company, memberTitle, companyTitle, backgroundColor}) => {
  return (
    <>
      <MenuItem sx={{minWidth: 200, backgroundColor}}>
        <Stack direction="row" spacing={2}>
          <Typography variant="subtitle2" sx={{width: 120}}>{companyName}</Typography>
          <Typography variant="subtitle2" sx={{width: 50, textAlign: 'center'}}>{member || memberTitle}</Typography>
          <Typography variant="subtitle2" sx={{width: 50, textAlign: 'center'}}>{company || companyTitle}</Typography>
        </Stack>
      </MenuItem>
      {companyTitle && <Divider sx={{margin: '0!important'}}/>}
    </>
  )
}

export default EmployeeTable